import { c as _c } from "react/compiler-runtime";
import { useDashboardRouter as useRouter } from "@/hooks/useDashboardRouter";
import { useDashboardSearchParams } from "@/hooks/useDashboardNavigation";
import { useActiveGithubOrgForUserNonSuspense } from "@/hooks/useConnectedGithubOrgs";
import { ADMIN_SECTIONS, type AdminTabValue, adminTabFromParam, adminTabHref } from "@/components/admin/adminSections";
import { cn } from "@repo/blacksmith-ui/cn";
import { RoutePrefetchLink } from "../ui/route-prefetch-link";
import { Sidebar as ShadcnSidebar, SidebarContent, SidebarFooter, SidebarGroup, SidebarGroupContent, SidebarHeader, SidebarMenu, SidebarMenuButton, SidebarMenuItem, SidebarRail } from "../ui/sidebar";
import { AppSidebarHeader, AppSidebarHeaderActions } from "../ui/app-header";
import { useCodesmithSidebarVariant } from "@/components/codesmith/useCachedSidebarRedesignEnabled";
import { SidebarHoverCardMenuItem } from "./SidebarHoverCardMenuItem";
const menuButtonClassName = "h-8 rounded-md p-1.5 group-data-[state=collapsed]:p-0 group-data-[state=collapsed]:!w-8 group-data-[state=collapsed]:!h-8 group-data-[state=collapsed]:!max-w-8 group-data-[state=collapsed]:!min-w-8 group-data-[collapsible=icon]:!w-8 group-data-[collapsible=icon]:!h-8 group-data-[collapsible=icon]:!max-w-8 group-data-[collapsible=icon]:!min-w-8 group-data-[state=collapsed]:justify-center group-data-[state=collapsed]:mx-auto type-body-s-regular text-neutral-800 hover:bg-black/[0.04] hover:text-neutral-950 data-[active=true]:bg-[#0000000f] data-[active=true]:text-neutral-950 [&_svg]:text-neutral-600 hover:[&_svg]:text-neutral-900 data-[active=true]:[&_svg]:text-neutral-900 dark:text-neutral-300 dark:hover:bg-white/[0.04] dark:hover:text-white dark:data-[active=true]:bg-white/6 dark:data-[active=true]:text-white dark:[&_svg]:text-neutral-400 dark:hover:[&_svg]:text-white dark:data-[active=true]:[&_svg]:text-white cursor-pointer";

/** The admin tab the current route shows; the internal-monitors org pages belong to Internal Monitors. */
function useActiveAdminTab() {
  const $ = _c(3);
  const router = useRouter();
  const t0 = useDashboardSearchParams();
  let t1;
  if ($[0] !== router.pathname || $[1] !== t0) {
    t1 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const tabParam = t0.get("tab");
      if (router.pathname === "/admin") {
        t1 = adminTabFromParam(tabParam).value;
        break bb0;
      }
    }
    $[0] = router.pathname;
    $[1] = t0;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  if (t1 !== Symbol.for("react.early_return_sentinel")) {
    return t1;
  }
  if (router.pathname.startsWith("/admin/internal-monitors")) {
    return "internal-monitors";
  }
  return null;
}

/**
 * Contextual sidebar for the superuser admin routes. Sits to the right of the
 * app-switcher rail with the same rows as the GitHub Actions sidebar; Proof
 * Analysis opens its pages in a hover card like Run History does.
 */
export default function AdminSidebar() {
  const $ = _c(19);
  const {
    activeNamedOrg
  } = useActiveGithubOrgForUserNonSuspense();
  const redesign = useCodesmithSidebarVariant(activeNamedOrg) === "redesign";
  const activeTab = useActiveAdminTab();
  const rowFillInsetClass = redesign && "group-data-[state=expanded]:border-y-2 group-data-[state=expanded]:bg-clip-padding";
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = cn("left-[var(--app-switcher-rail-width,_48px)] transition-[left] duration-[240ms] ease-[cubic-bezier(0.22,1,0.36,1)]");
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <SidebarHeader className="shrink-0 bg-sidebar p-0"><AppSidebarHeader title="Admin" actions={<AppSidebarHeaderActions />} collapsible={true} /></SidebarHeader>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = redesign ? "pt-0" : "pt-[calc(var(--spacing-app-header-gap)-0.5rem)]";
  let t3;
  if ($[2] !== t2) {
    t3 = cn("border-0 min-h-0 flex-1 overflow-y-auto group-data-[state=collapsed]:pl-3 group-data-[state=collapsed]:pr-1", t2);
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const t4 = redesign ? "gap-0" : "gap-1";
  let t5;
  if ($[4] !== t4) {
    t5 = cn("flex flex-col", t4);
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== activeTab || $[7] !== rowFillInsetClass) {
    t6 = ADMIN_SECTIONS.map(section => {
      const Icon = section.icon;
      if ("subTabs" in section) {
        return <SidebarHoverCardMenuItem key={section.label} name={section.label} icon={<Icon className="size-[18px]" />} subItems={section.subTabs.map(subTab => ({
          name: subTab.label,
          href: adminTabHref(subTab.value),
          current: subTab.value === activeTab,
          icon: <subTab.icon className="size-4" />
        }))} prefetchOnIntent={false} buttonClassName={rowFillInsetClass} />;
      }
      return <SidebarMenuItem key={section.value} className="group-data-[state=collapsed]:flex group-data-[state=collapsed]:justify-center group-data-[state=collapsed]:w-full -mx-1"><SidebarMenuButton asChild={true} isActive={section.value === activeTab} tooltip={section.label} className={cn(menuButtonClassName, rowFillInsetClass)}><RoutePrefetchLink href={adminTabHref(section.value)} prefetchOnIntent={false} aria-label={section.label} className="cursor-pointer"><div className="relative"><Icon className="size-[18px]" /></div><span className="whitespace-nowrap group-data-[state=collapsed]:hidden">{section.label}</span></RoutePrefetchLink></SidebarMenuButton></SidebarMenuItem>;
    });
    $[6] = activeTab;
    $[7] = rowFillInsetClass;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t5 || $[10] !== t6) {
    t7 = <SidebarGroupContent className="flex flex-1 flex-col px-1 py-0 group-data-[state=collapsed]:items-center group-data-[state=collapsed]:px-0"><SidebarMenu className={t5}>{t6}</SidebarMenu></SidebarGroupContent>;
    $[9] = t5;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t3 || $[13] !== t7) {
    t8 = <SidebarContent className="flex min-h-[200px] flex-col gap-0 overflow-hidden bg-sidebar py-0 group-data-[state=collapsed]:px-0 group-data-[collapsible=icon]:overflow-hidden"><SidebarGroup className={t3}>{t7}</SidebarGroup></SidebarContent>;
    $[12] = t3;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t10;
  let t9;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <SidebarFooter className="shrink-0 gap-0 p-0 group-data-[state=collapsed]:border-t-0" />;
    t10 = <SidebarRail />;
    $[15] = t10;
    $[16] = t9;
  } else {
    t10 = $[15];
    t9 = $[16];
  }
  let t11;
  if ($[17] !== t8) {
    t11 = <ShadcnSidebar collapsible="icon" className={t0}>{t1}{t8}{t9}{t10}</ShadcnSidebar>;
    $[17] = t8;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  return t11;
}